import MailOutlineIcon from '@mui/icons-material/MailOutline';
import SendIcon from '@mui/icons-material/Send';
import DoneIcon from '@mui/icons-material/Done';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import CancelIcon from '@mui/icons-material/Cancel';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { AdminExternalPurchaseInfo, PurchaseSource } from './externalMarkets';
import { AdminOrderPromoCode } from './promoCode';

export interface GuaranteedEntity
{
    requestedFromSellerAt?: Date,
    receivedItemsAt?: Date,
    guaranteedUpTo?: Date,
    sentToBuyerAt?: Date
}

export interface OrderGeneralInfo {
    onSale: number;
    sold: number;
}

export interface OrderLineItem
{
    id: number,
    skinId: number,
    image: string;
    skinName: string,
    assetId: bigint,
    price: number,
    commission: number,
    currency: string
}

export interface OrderDetailsInfo extends GuaranteedEntity
{
    id: number;
    orderNumber: string;
    groupId?: number;
    bidId?: number;
    /** Заказ создан по офлайн-заявке (покупатель не в сети, офлайн-заявки включены) */
    byOfflineBid?: boolean;
    gameId: number;
    gameName: string;
    sellerId: bigint;
    buyerId: bigint;
    botId?: number;
    botSteamId?: bigint;
    type: OrderType;
    status: OrderStatus;
    createdAt: Date;
    completedAt?: Date;
    total: number;
    feeAmount: number;
    failReason?: OrderFailReason;
    comment?: string;
    items: OrderLineItem[],
    /** Откуда предметы: живой продавец или поставщик */
    source?: PurchaseSource;
    externalMarketKey?: string;
    externalMarketName?: string;
    /** Выкуп у поставщика: цена, курс, план/факт, трейды поставщика */
    externalPurchase?: AdminExternalPurchaseInfo;
    /** Скины заказа остались на боте и могут быть переданы вручную */
    skinsLeftOnBot: boolean;
    /** Промокоды, израсходованные на заказ; пусто — без промокода */
    promoCodes?: AdminOrderPromoCode[];
}

export interface OrderListItem extends GuaranteedEntity
{
    id: number;
    orderId: number;
    orderNumber: string;
    orderGroupId?: number;
    skinId: number;
    image: string;
    skinName: string;
    gameId: number;
    gameName: string;
    status: OrderStatus;
    assetId: number;
    botId?: bigint;
    bidId?: number;
    botSteamId?: bigint;
    sellerId: bigint;
    buyerId: bigint;
    type: OrderType;
    price: number;
    currency: string;
    failReason?: OrderFailReason;
    comment?: string;
    commission: number;
    createdAt: Date;
    completedAt?: Date;
    /** Промокоды, израсходованные на заказ; пусто — без промокода */
    promoCodes?: AdminOrderPromoCode[];
}

export interface OrderListResult {
    items: OrderListItem[],
    count: number,
}

export const OrderStatusDescription = {
    0: 'Новый',
    1: 'В очереди на обработку',
    2: 'Ожидание ответа продавца',
    3: 'Предметы получены от продавца',
    4: 'Ожидание ответа покупателя',
    5: 'Завершен',
    6: 'Ошибка',
    7: 'Ручной режим',
    8: 'В процессе отмены',
    9: 'Возврат'
}

export const OrderTypeDescription = {
    0: 'Через бота',
    1: 'P2P',
    2: 'Поставщик',
}

export interface OrderStatistic {
    hour: string;
    totalCount: number;
    totalSum: number;
    csCount: number;
    csSum: number;
    dota2Count: number;
    dota2Sum: number;
    tf2Count: number;
    tf2Sum: number;
    giftCount: number;
    giftSum: number;
    rustCount: number;
    rustSum: number;
    /** Из них куплено через поставщиков */
    externalCount: number;
    externalSum: number;
    date:Date;
}

export enum OrderType {
    ViaBot = 0,
    P2P = 1,
    ExternalMarket = 2
}

export enum OrderStatus {
    New = 0,
    Pending = 1,
    WaitingForSeller = 2,
    SentBySeller = 3,
    WaitingForBuyer = 4,
    Completed = 5,
    Failed = 6,
    ManualMode = 7,
    Cancelling = 8,
    Refunded = 9
}

export namespace OrderStatus {

    export function isCompleted(status:OrderStatus): boolean {
        return status === OrderStatus.Completed || status === OrderStatus.Failed || status === OrderStatus.Refunded;
    }

    export function getIcon(status?: OrderStatus): any {
        switch(status) { 
            case OrderStatus.New: { 
                return MailOutlineIcon;
            }
            case OrderStatus.Pending:
            case OrderStatus.WaitingForSeller:
            case OrderStatus.SentBySeller:
            case OrderStatus.WaitingForBuyer: { 
                return SendIcon;
            }
            case OrderStatus.Completed: {
                return DoneIcon;
            }
            case OrderStatus.Refunded: {
                return CurrencyExchangeIcon;
            }
            case OrderStatus.Cancelling:
            case OrderStatus.Failed: {
                return CancelIcon;
            } 
            case OrderStatus.ManualMode: {
                return NewReleasesIcon;
            }
            default: { 
                return QuestionMarkIcon;
            }
        }
    }

    export function getColor (status?: OrderStatus): 'primary' | 'neutral' | 'danger' | 'success' | 'warning' {
        switch(status) { 
            case OrderStatus.Pending:
            case OrderStatus.WaitingForSeller:
            case OrderStatus.SentBySeller:
            case OrderStatus.WaitingForBuyer: { 
                return 'primary';
            }
            case OrderStatus.Completed:
            case OrderStatus.Refunded: {
                return 'success';
            }
            case OrderStatus.Failed: {
                return 'danger';
            } 
            case OrderStatus.Cancelling:
            case OrderStatus.ManualMode: {
                return 'warning';
            }
            case OrderStatus.New:
            default: { 
                return 'neutral';
            }
        }
    }
}

export namespace GuaranteedEntity  {
    export function isGuaranteed(entity: GuaranteedEntity): boolean {
        return (entity.receivedItemsAt && entity.guaranteedUpTo && new Date(entity.guaranteedUpTo) >= new Date()) || false;
    }
}

export enum OfferType {
    ToBuyer = 0,
    ToSeller = 1,
    P2P = 2,
    FromExternalMarket = 3
}

export enum OfferStatus {
    New = 0,
    CanBeSend = 1,
    WaitingForConfirmation = 2,
    Sent = 3,
    Accepted = 4,
    WaitingForCancellation = 5,
    Canceled = 6,
    InvalidItems = 7,
    Hold = 8,
    RolledBack = 9,
    Undefined = 10,
    Paused = 11
}

export const OfferTypeDescription = {
    0: 'Бот -> Покупатель',
    1: 'Продавец <- Бот',
    2: 'Продавец -> Покупатель',
    3: 'Поставщик -> Бот'
}

export const OfferStatusDescription = {
    0: 'Новый',
    1: 'Проверено. Готово к отправке',
    2: 'Ожидает подтверждения',
    3: 'Сделка создана',
    4: 'Сделка принята партнером',
    5: 'Ожидает отмены',
    6: 'Отменено',
    7: 'Проблема с предметами сделки',
    8: 'Сделка заморожена (HOLD)',
    9: 'Произошел откат сделки',
    10: 'Статус не определен',
    11: 'На паузе'
}

export const OfferHandleModeDescription = {
    0: 'Мгновенная',
    1: 'Ручная',
    2: 'По времени'
}

export enum SteamApiTradeOfferState
{
    Unknown = 0,
    Invalid = 1,
    Active = 2,
    Accepted = 3,
    Countered = 4,
    Expired = 5,
    Canceled = 6,
    Declined = 7,
    InvalidItems = 8,
    CreatedNeedsConfirmation = 9,
    CanceledBySecondFactor = 10,
    InEscrow = 11
}

export enum SteamApiTradeStatus
{
    Init = 0,
    PreCommitted = 1,
    Committed = 2,
    Complete = 3,
    Failed = 4,
    PartialSupportRollback = 5,
    FullSupportRollback = 6,
    SupportRollback_Selective = 7,
    RollbackFailed = 8,
    RollbackAbandoned = 9,
    InEscrow = 10,
    EscrowRollback = 11
}

export enum OfferHandleMode
{
    Instant = 0,
    Manually = 1,
    ByTime = 2
}

export interface OfferInfo
{
    id: number;
    created: Date;
    updated: Date;
    expireAt?: Date;
    type: OfferType;
    status: OfferStatus;
    steamStatus?: SteamApiTradeOfferState;
    steamTradeStatus?: SteamApiTradeStatus;
    turnOnManualMode: boolean;
    userId: bigint;
    sentAt?: Date;
    lastError?: string;
    botId?: number;
    steamOfferId?: bigint;
    steamTradeId?: bigint;
    cancellationReason?: OfferCancellationReason;
    groupId?: string;
    handleMode?: OfferHandleMode;
    handleDelaySeconds?: number;
}

export interface OfferHistoryInfo
{
    id: number;
    created: Date;
    status: OfferStatus;
    steamStatus?: SteamApiTradeOfferState;
    steamTradeStatus?: SteamApiTradeStatus;
    steamOfferId?: bigint;
    steamTradeId?: bigint;
    cancellationReason?: OfferCancellationReason;
    errorMessage?: string;
    botId?: number;
    assetsStatesJson?: string;
    groupId?: string;
}

export enum OfferCancellationReason {
    NotCreated = 0,
    NotConfirmedBySeller = 1,
    ExpiredByBuyer = 2,
    ExpiredBySeller = 3,
    ConfirmationDeclined = 4,
    CancelledBySeller = 5,
    DeclinedOrCountered = 6,
    StatusUndefined = 7,
    SellerTradeLinkIsInvalid = 8,
    BuyerTradeLinkIsInvalid = 9,
    BotHasHold = 10,
    SellerIsOffline = 11,
    MissingItemsForTrade = 12,
    FullInventory = 13,
    ConfirmationFailure = 14,
    TradeIssues = 15,
    SellerSteamBan = 16,
    BuyerSteamBan = 17,
    SellerSteamGuardIssue = 18,
    BuyerSteamGuardIssue = 19,
    SellerPrivateInventory = 20,
    BuyerPrivateInventory = 21,
    SellerHasHold = 22,
    BuyerHasHold = 23,
    Undefined = 24,
    Manually = 25,
    BotSteamBan = 26,
    SellerVACBan = 27,
    BuyerVACBan = 28,
    SellerApiKeyIsInvalid = 29,
    ReceiveItemsBlock = 30,
    NoBotsAvailable = 31,
    AssignedBotIsUnavailable = 32,
    PartnerOffersLimit = 33,
    P2PDuplicate = 34,
    SellerLimitedAccount = 35,
    ExtensionIssues = 36,
    BotTradeLinkIsInvalid = 37,
    TradeHold = 38,
    OffersLimit = 39,
    ExternalMarketTradeExpired = 40
}

export const OfferCancellationReasonDescription = {
    0: 'Не удалось создать сделку',
    1: 'Продавец не подтвердил сделку',
    2: 'Таймаут сделки по вине покупателя',
    3: 'Таймаут сделки по вине продавца',
    4: 'Подтверждение сделки отклонено',
    5: 'Сделка отменена продавцом',
    6: 'Сделка отменена или сделано встречное предложение',
    7: 'Не удалось определить статус сделки в Steam',
    8: 'Троговая ссылка продавца невалидна',
    9: 'Троговая ссылка покупателя невалидна',
    10: 'Сделка заморожена (HOLD бота)',
    11: 'Продавец не в сети',
    12: 'Предметы для сделки отсутствуют в инвентаре',
    13: 'Полный инвентарь',
    14: 'Не удалось подтвердить сделку',
    15: 'Проблемы с проверкой трейда',
    16: 'SteamBAN продавца',
    17: 'SteamBAN покупателя',
    18: 'Проблема со SteamGuard продавца (не установлен или активирован менее 7 дней назад)',
    19: 'Проблема со SteamGuard покупателя (не установлен или активирован менее 7 дней назад)',
    20: 'Приватный инвентарь продавца',
    21: 'Приватный инвентарь покупателя',
    22: 'Инвентарь продавца заморожен (HOLD)',
    23: 'Инвентарь покупателя заморожен (HOLD)',
    24: 'Причина отмены не определена',
    25: 'Заказ отменен вручную',
    26: 'SteamBAN бота',
    27: 'VAC BAN продавца',
    28: 'VAC BAN покупателя',
    29: 'АПИ ключ продавца невалиден',
    30: 'Блок приема предметов',
    31: 'Нет доступных ботов',
    32: 'Назначенный бот недоступен',
    33: 'Лимит оферов по партнеру',
    34: 'Обнаружен дубликат P2P сделки',
    35: 'Ограниченный аккаунт продавца',
    36: 'Проблемы с расширением (возможно, ошибка авторизации)',
    37: 'Невалидная торговая ссылка бота',
    38: 'Заморозка сделки',
    39: 'Лимит входящих сделок',
    40: 'Входящий офер поставщика не принят вовремя'
}

export enum OrderFailReason {
    Undefined = 0,
    ExpiredBySeller = 1,
    ExpiredByBuyer = 2,
    DeclinedBySeller = 3,
    DeclinedByBuyer = 4,
    SellerTradeLinkIsInvalid = 5,
    BuyerTradeLinkIsInvalid = 6,
    BotHasHold = 7,
    SellerHasHold = 8,
    BuyerHasHold = 9,
    SellerIsOffline = 10,
    MissingItemsForTrade = 11,
    BuyerFullInventory = 12,
    SellerSteamBan = 13,
    BuyerSteamBan = 14,
    BotSteamBan = 15,
    SellerVacBan = 16,
    BuyerVacBan = 17,
    SellerSteamGuardIssue = 18,
    BuyerSteamGuardIssue = 19,
    SellerPrivateInventory = 20,
    BuyerPrivateInventory = 21,
    BySellerRequest = 22,
    Manually = 23,
    SellerApiKeyIsInvalid = 24,
    ReceiveItemsBlock = 25,
    NoBotsAvailable = 26,
    BotIssue = 27,
    Refunded = 28,
    ExtensionIssues = 29,
    SellerLimitedAccount = 30,
    OffersLimit = 31,
    TradeHold = 32,
    ExternalPurchaseFailed = 33,
}

export const OrderFailReasonDescription = {
    0: 'Причина не определена',
    1: 'Таймаут по вине продавца',
    2: 'Таймаут по вине покупателя',
    3: 'Отклонено/не подтверждено продавцом',
    4: 'Отклонено/не подтверждено покупателем',
    5: 'Невалидная торговая ссылка продавца',
    6: 'Невалидная торговая ссылка покупателя',
    7: 'Холд бота',
    8: 'Холд продавца',
    9: 'Холд покупателя',
    10: 'Продавец не в сети',
    11: 'Отсутствуют предметы для сделки',
    12: 'Инвентарь покупателя переполнен',
    13: 'Steamban продавца',
    14: 'Steamban покупателя',
    15: 'Steamban бота',
    16: 'Vacban продавца',
    17: 'Vacban покупателя',
    18: 'Проблема со SteamGuard продавца (не установлен или активирован менее 7 дней назад)',
    19: 'Проблема со SteamGuard покупателя (не установлен или активирован менее 7 дней назад)',
    20: 'Приватный инвентарь продавца',
    21: 'Приватный инвентарь покупателя',
    22: 'Отмена по запросу продавца',
    23: 'Ручная отмена администратором',
    24: 'Невалидный АПИ ключ продавца',
    25: 'Блок приема предметов покупателем',
    26: 'Нет свободных ботов',
    27: 'Проблемы с ботом',
    28: 'Возмещение средств покупателю',
    29: 'Проблемы с расширением (возможно, ошибка авторизации)',
    30: 'Ограниченный аккаунт продавца',
    31: 'Лимит входящих сделок партнера',
    32: 'Заморозка сделки',
    33: 'Не удалось выкупить у поставщика'
}

export enum ForceActionType
{
    Handle = 0,
    Cancel = 1,
    Complete = 2,
    Refund = 3
}

export const ForceActionName = {
    0: 'Запустить обработку',
    1: 'Отменить',
    2: 'Завершить',
    3: 'Возместить'
}

export const ForceActionDescription = {
    0: 'Будет запущена внеочередная обработка ботом',
    1: 'Перевод в статус "ОШИБКА", возврат денег покупателю',
    2: 'Перевод в статус "УСПЕХ", деньги покупателю не возвращаются',
    3: 'Перевод в статус "ВОЗВРАТ", возврат денег покупателю'
}

export interface ForceOrderResponse
{
    orderId: number,
    success: boolean,
    error?: string
}

export interface ForceOrderRequest
{
    orderId: number,
    actionType: ForceActionType,
    reason?: string
}